import React, {useState} from 'react';
import {Box, IconButton, Tooltip} from '@mui/material';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {styled} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import ConfirmationModal from "../../../../shared/modals/ConfirmationModal";
import {AssetBoxContainer, AssetBoxWrapper, AssetLabel, UsedByBadge, UsedByChip} from "./AssetBoxStyle";
import GenericEditNameModal from "../modals/GenericEditNameModal";
import Asset from "../../../../../models/Asset";
import {UsedByEntry} from "../hooks/useUsedAssets";


const StatusIndicator = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'bgColor',
})<{ bgColor?: string }>(({theme, bgColor}) => ({
    fontSize: '14px',
    width: '85%',
    marginTop: '8px',
    margin: '0 auto',
    padding: '0',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: bgColor || theme.palette.primary.main,
    color: '#FFFFFF',
    fontWeight: 500,
}));

const StatusWrapper = styled(Box)(({theme}) => ({
    position: 'absolute',
    bottom: 0,
    width: '100%',
    height: '40px',
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: "#05101db8",
}));

const HoverControls = styled(Box)({
    position: 'absolute',
    top: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '4px',
    justifyContent: 'space-between',
    width: '100%',
    background: 'linear-gradient(180deg, rgba(0,0,0,0.5), transparent)',
});

// Asset Box Image Component
interface AssetBoxImageProps {
    isSelected: boolean;
    asset: any;
    handleImageClick: (asset: any) => void;
    handleImageDoubleClick?: (asset: any) => void;
    handleImageDelete: (asset: any) => void;
    handleImageEdit: (asset: any) => void;
    status?: 'uploaded' | 'uploading' | 'failed' | null;
    usedByInputs?: UsedByEntry[];
    /**
     * Run the AI edit on THIS already-stored photo. Absent unless the field is
     * AI-enabled and the team has quota.
     *
     * Without it the AI edit was reachable only in the moment right after an
     * upload, so every photo already in the library — the whole point of having
     * a library — could never be edited without uploading it a second time.
     */
    onAiEdit?: (asset: any) => void;
}

export const AssetBoxImage: React.FC<AssetBoxImageProps> = ({
                                                                isSelected,
                                                                asset,
                                                                handleImageClick,
                                                                handleImageDoubleClick,
                                                                handleImageDelete,
                                                                handleImageEdit,
                                                                status,
                                                                usedByInputs,
                                                                onAiEdit,
                                                            }) => {
    const [isHovering, setIsHovering] = useState(false);
    const [isEditDialogOpen, setEditDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const {t} = useTranslation();

    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);

    const handleEditDialogOpen = () => setEditDialogOpen(true);
    const handleDeleteDialogOpen = () => setDeleteDialogOpen(true);
    const handleDeleteDialogClose = () => setDeleteDialogOpen(false);

    const confirmDelete = () => {
        handleImageDelete(asset);
        handleDeleteDialogClose();
    };

    const getStatusColor = () => {
        switch (status) {
            case 'uploaded':
                return '#2196f3'; // Blue
            case 'uploading':
                return '#9e9e9e'; // Gray
            case 'failed':
                return '#f44336'; // Red
            default:
                return '#2196f3'; // Default blue
        }
    };

    const getStatusIcon = () => {
        switch (status) {
            case 'uploaded':
                return <CheckIcon style={{fontSize: "15px"}} sx={{mr: 0.5}}/>;
            case 'uploading':
                return null;
            case 'failed':
                return <CloseIcon style={{fontSize: "15px"}} sx={{mr: 0.5}}/>;
            default:
                return null;
        }
    };

    const getStatusText = () => {
        switch (status) {
            case 'uploaded':
                return t('editor.imageSelector.status.uploaded');
            case 'uploading':
                return t('editor.imageSelector.status.uploading');
            case 'failed':
                return t('editor.imageSelector.status.failed');
            default:
                return '';
        }
    };

    return (
        <AssetBoxContainer
            item
            xs={4}
            md={3}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <AssetBoxWrapper
                onClick={() => handleImageClick(asset)}
                onDoubleClick={() => handleImageDoubleClick && handleImageDoubleClick(asset)}
                sx={{
                    "&::after": {
                        content: '""',
                        position: 'absolute',
                        top:0,
                        bottom:0,
                        right:0,
                        left:0,
                        border: isSelected ? '3px solid #2196f3':'3px solid #ffffff78',
                        borderRadius: '16px',
                        pointerEvents: 'none'
                    }
                }}
            >
                <img
                    src={asset.thumbnail}
                    alt={asset.name}
                    loading="lazy"
                    width="100%"
                    height="100%"
                    style={{
                        display: 'block',
                        objectFit: 'cover',
                    }}
                />

                {isHovering && (
                    <HoverControls>
                        <IconButton
                            size="small"
                            sx={{color: 'white'}}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEditDialogOpen();
                                setTimeout(() => setIsHovering(false), 100);
                            }}
                        >
                            <EditIcon fontSize="small"/>
                        </IconButton>
                        {onAiEdit && (
                            <Tooltip title={t('aiEdit.editThisPhoto')}>
                                <IconButton
                                    size="small"
                                    sx={{color: '#7CE0A6'}}
                                    aria-label={t('aiEdit.editThisPhoto')}
                                    data-testid="asset-ai-edit"
                                    onClick={(e) => {
                                        // The tile itself selects the asset; this
                                        // must not double as a selection click.
                                        e.stopPropagation();
                                        onAiEdit(asset);
                                        setTimeout(() => setIsHovering(false), 100);
                                    }}
                                >
                                    <AutoAwesomeIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        )}
                        <IconButton
                            size="small"
                            sx={{color: 'white'}}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteDialogOpen();
                                setTimeout(() => setIsHovering(false), 100);
                            }}
                        >
                            <DeleteIcon fontSize="small"/>
                        </IconButton>
                    </HoverControls>
                )}

                {status && (
                    <StatusWrapper>
                        <StatusIndicator color={getStatusColor()}>
                            {getStatusIcon()}
                            {getStatusText()}
                        </StatusIndicator>
                    </StatusWrapper>
                )}

                {!status && usedByInputs && usedByInputs.length > 0 && (
                    <UsedByBadge>
                        {usedByInputs.map((entry) => (
                            <UsedByChip key={entry.inputKey}>{entry.label}</UsedByChip>
                        ))}
                    </UsedByBadge>
                )}

            </AssetBoxWrapper>
            <AssetLabel>{asset.name}</AssetLabel>

            {isEditDialogOpen && <GenericEditNameModal<Asset>
                entity={asset}
                open={isEditDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                onEntityEdited={handleImageEdit}
                apiEndpoint="/asset"
                dialogTitle="ערוך שם לתמונה"
                successMessage="התמונה נערכה בהצלחה"
                errorMessage="תקלה בעת עריכת התמונה"
                placeholder="שם התמונה"
                inputError="שם התמונה נדרש"
            />}

            {isDeleteDialogOpen && <ConfirmationModal
                show={isDeleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={confirmDelete}
                title="אישור מחיקה"
                message={`האם אתה בטוח שאתה רוצה למחוק את התמונה "${asset.name}"?`}
            />}
        </AssetBoxContainer>
    );
};

// Memoized: the grid re-renders on every scroll/fetch page, but a tile only needs
// to update when its own asset/selection/status/usedBy props change.
export default React.memo(AssetBoxImage);
